// app/components/home/Bamboo.tsx
"use client";

import React, { CSSProperties, useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Card, CardContent, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Mousewheel, EffectFade, Pagination, Navigation } from "swiper/modules";
import "swiper/css/bundle";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function Bamboo() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const bambooFixedBgrRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  // ---- fixed background เดิม ----
  useEffect(() => {
    const handleScroll = () => {
      if (!bambooFixedBgrRef.current) return;
      const introTop = bambooFixedBgrRef.current.getBoundingClientRect().top;
      if (introTop <= 0) {
        bambooFixedBgrRef.current.classList.add("bg-fixed");
      } else {
        bambooFixedBgrRef.current.classList.remove("bg-fixed");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ path จริงของหน้า (ต้องตรงกับตอน save)
  const [pagePath, setPagePath] = useState<string>("/");

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
  }, []);

  // ---- default text จาก i18n (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("Bamboo.title");
  const defaultSubtitle = t("Bamboo.subtitle");
  const defaultCard1Title = t("Bamboo.Card1.title");
  const defaultCard1Content = t("Bamboo.Card1.content");
  const defaultCard2Title = t("Bamboo.Card2.title");
  const defaultCard2Content = t("Bamboo.Card2.content");
  const defaultCard3Title = t("Bamboo.Card3.title");
  const defaultCard3Content = t("Bamboo.Card3.content");
  const defaultCard4Title = t("Bamboo.Card4.title");
  const defaultCard4Content = t("Bamboo.Card4.content");

  // ---- default images ----
  const defaultCassavaImg = "/assets/home/bamboo/cassava1.png";
  const defaultFertilizerImg = "/assets/home/bamboo/fertilizer.png";

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [card1Title, setCard1Title] = useState(defaultCard1Title);
  const [card1Content, setCard1Content] = useState(defaultCard1Content);
  const [card2Title, setCard2Title] = useState(defaultCard2Title);
  const [card2Content, setCard2Content] = useState(defaultCard2Content);
  const [card3Title, setCard3Title] = useState(defaultCard3Title);
  const [card3Content, setCard3Content] = useState(defaultCard3Content);
  const [card4Title, setCard4Title] = useState(defaultCard4Title);
  const [card4Content, setCard4Content] = useState(defaultCard4Content);

  const [cassavaImg, setCassavaImg] = useState(defaultCassavaImg);
  const [fertilizerImg, setFertilizerImg] = useState(defaultFertilizerImg);

  // เมื่อ locale เปลี่ยน → reset กลับค่า default ก่อน
  useEffect(() => {
    setTitle(defaultTitle);
    setSubtitle(defaultSubtitle);
    setCard1Title(defaultCard1Title);
    setCard1Content(defaultCard1Content);
    setCard2Title(defaultCard2Title);
    setCard2Content(defaultCard2Content);
    setCard3Title(defaultCard3Title);
    setCard3Content(defaultCard3Content);
    setCard4Title(defaultCard4Title);
    setCard4Content(defaultCard4Content);
    setCassavaImg(defaultCassavaImg);
    setFertilizerImg(defaultFertilizerImg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (!pagePath) return;

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${
            process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID
          }&path=${encodeURIComponent(pagePath)}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback: ดึงจาก selector เดิม ถ้ายังไม่มี cmsKey ใน DB
          if (!key) {
            const match = e.selector?.match(/data-cms-key="([^"]+)"/);
            if (match) {
              key = match[1];
            }
          }

          if (key) map[key] = e.value;
        });

        // text: title + subtitle
        if (map["home.bamboo.title"]) setTitle(map["home.bamboo.title"]);
        if (map["home.bamboo.subtitle"])
          setSubtitle(map["home.bamboo.subtitle"]);

        // cards
        if (map["home.bamboo.card1.title"])
          setCard1Title(map["home.bamboo.card1.title"]);
        if (map["home.bamboo.card1.content"])
          setCard1Content(map["home.bamboo.card1.content"]);

        if (map["home.bamboo.card2.title"])
          setCard2Title(map["home.bamboo.card2.title"]);
        if (map["home.bamboo.card2.content"])
          setCard2Content(map["home.bamboo.card2.content"]);

        if (map["home.bamboo.card3.title"])
          setCard3Title(map["home.bamboo.card3.title"]);
        if (map["home.bamboo.card3.content"])
          setCard3Content(map["home.bamboo.card3.content"]);

        if (map["home.bamboo.card4.title"])
          setCard4Title(map["home.bamboo.card4.title"]);
        if (map["home.bamboo.card4.content"])
          setCard4Content(map["home.bamboo.card4.content"]);

        // images
        if (map["home.bamboo.cassava"]) {
          setCassavaImg(map["home.bamboo.cassava"]);
        }
        if (map["home.bamboo.fertilizer"]) {
          setFertilizerImg(map["home.bamboo.fertilizer"]);
        }
      } catch (err) {
        console.warn("[Bamboo] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  const slideHeights: { [key: number]: string } = {
    0: "600px",
    1: "700px",
    2: "850px",
    3: "1200px",
  };

  const swiperStyle = {
    "--swiper-height": slideHeights[activeSlide] || "600px",
  } as CSSProperties;

  return (
    <>
      <style>
        {`
          .bamboo-wrap .swiper-button-prev, 
          .bamboo-wrap .swiper-button-next { 
            display: none; 
          }
          @media (max-width: 599px) {
            .bamboo-wrap .swiper-bamboo { height: var(--swiper-height, 600px); transition: height 0.5s ease-in-out; }
            .bamboo-wrap .swiper-button-prev, 
            .bamboo-wrap .swiper-button-next { 
              display: flex;
              color: #fff;
              width: 40px;
              height: 40px;
              font-weight: 600px;
              background: #00000050;
              border-radius: 50%;
              padding: 8px;
            }
            .bamboo-wrap .swiper-pagination { 
              display: none; 
            }
          }
        `}
      </style>
      <Box
        className={locale === "en" ? "bamboo-wrap" : "bamboo-wrap bamboo-wrap-zh"}
        ref={bambooFixedBgrRef}
        style={swiperStyle}
      >
        <Swiper
          onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
          navigation={true}
          //direction="vertical"
          effect="fade"
          slidesPerView={1}
          loop={false}
          mousewheel={{
            releaseOnEdges: true,
          }}
          pagination={{
            clickable: true,
          }}
          autoplay={{
            delay: 9999995000,
          }}
          modules={[Autoplay, Mousewheel, EffectFade, Pagination, Navigation]}
        >
          <SwiperSlide>
            <Box
              className="swiper-bamboo"
              style={{
                backgroundImage: `url('/assets/home/bamboo/cassava1.png')`,
              }}
            >
              <Container className="container" sx={{ py: {xs: 5, lg: 30} }}>
                <Typography
                  variant="h2"
                  data-heading-tag="H2"
                  sx={{
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right",
                  }}
                  data-cms-key="home.bamboo.title"
                  data-aos="fade-up"
                >
                  {title}
                </Typography>
                <Typography
                  variant="h3"
                  data-heading-tag="H3"
                  data-cms-key="home.bamboo.subtitle"
                  data-aos="fade-up"
                  sx={{
                    mb: 5,
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right"
                  }}>
                  {subtitle}
                </Typography>
                <Grid
                  container
                  spacing={{xs:0, md:3, lg:0}}
                  sx={{ zIndex: 1, position: "relative" }}
                >
                  <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up">
                      <CardContent >
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card1.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card1Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card1.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card1Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </SwiperSlide>
          <SwiperSlide>
            <Box
              className="swiper-bamboo"
              style={{
                backgroundImage: `url('/assets/home/bamboo/cassava2.png')`,
              }}
            >
              <Container className="container" sx={{ py: {xs: 5, lg: 10} }}>
                <Typography
                  variant="h2"
                  data-heading-tag="H2"
                  sx={{
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right",
                  }}
                  data-cms-key="home.bamboo.title"
                  data-aos="fade-up"
                >
                  {title}
                </Typography>
                <Typography
                  variant="h3"
                  data-heading-tag="H3"
                  data-cms-key="home.bamboo.subtitle"
                  data-aos="fade-up"
                  sx={{
                    mb: 5,
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right"
                  }}>
                  {subtitle}
                </Typography>
                <Grid
                  container
                  spacing={{xs:0, md:3, lg:0}}
                  sx={{ zIndex: 1, position: "relative" }}
                >
                  <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up" sx={{ display: { xs: "none", md: "block" } }}>
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card1.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card1Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card1.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card1Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} offset={{ lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up">
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card2.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card2Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card2.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card2Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </SwiperSlide>
          <SwiperSlide>
            <Box
              className="swiper-bamboo"
              style={{
                backgroundImage: `url('/assets/home/bamboo/cassava3.png')`,
              }}
            >
              <Container className="container" sx={{ py: {xs: 5, lg: 10} }}>
                <Typography
                  variant="h2"
                  data-heading-tag="H2"
                  sx={{
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right",
                  }}
                  data-cms-key="home.bamboo.title"
                  data-aos="fade-up"
                >
                  {title}
                </Typography>
                <Typography
                  variant="h3"
                  data-heading-tag="H3"
                  data-cms-key="home.bamboo.subtitle"
                  data-aos="fade-up"
                  sx={{
                    mb: 5,
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right"
                  }}>
                  {subtitle}
                </Typography>
                <Grid
                  container
                  spacing={{xs:0, md:3, lg:0}}
                  sx={{ zIndex: 1, position: "relative" }}
                >
                  <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up" sx={{ display: { xs: "none", md: "block" } }}>
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card1.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card1Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card1.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card1Content}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card className="bamboo-card" data-aos="fade-up">
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card3.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card3Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card3.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card3Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} offset={{ lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up" sx={{ display: { xs: "none", md: "block" } }}>
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card2.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card2Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card2.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card2Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </SwiperSlide>
          <SwiperSlide>
            <Box
              className="swiper-bamboo"
              style={{
                backgroundImage: `url('/assets/home/bamboo/cassava4.png')`,
              }}
            >
              <Container className="container" sx={{ py: {xs: 5, lg: 10} }}>
                <Typography
                  variant="h2"
                  data-heading-tag="H2"
                  sx={{
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right",
                  }}
                  data-cms-key="home.bamboo.title"
                  data-aos="fade-up"
                >
                  {title}
                </Typography>
                <Typography
                  variant="h3"
                  data-heading-tag="H3"
                  data-cms-key="home.bamboo.subtitle"
                  data-aos="fade-up"
                  sx={{
                    mb: 5,
                    whiteSpace: { xs: "normal", lg: "pre-line" },
                    textAlign: "right"
                  }}>
                  {subtitle}
                </Typography>
                <Grid
                  container
                  spacing={{xs:0, md:3, lg:0}}
                  sx={{ zIndex: 1, position: "relative" }}
                >
                  <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up" sx={{ display: { xs: "none", md: "block" } }}>
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card1.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card1Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card1.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card1Content}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card className="bamboo-card" data-aos="fade-up" sx={{ display: { xs: "none", md: "block" } }}>
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card3.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card3Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card3.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card3Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} offset={{ lg: 4 }}>
                    <Card className="bamboo-card" data-aos="fade-up" sx={{ display: { xs: "none", md: "block" } }}>
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card2.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card2Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card2.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card2Content}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card className="bamboo-card" data-aos="fade-up">
                      <CardContent>
                        <Typography
                          variant="h4"
                          data-heading-tag="H4"
                          data-cms-key="home.bamboo.card4.title"
                          sx={{
                            m: 0
                          }}
                        >
                          {card4Title}
                        </Typography>
                        <Typography
                          variant="body1"
                          data-cms-key="home.bamboo.card4.content"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-line"
                          }}>
                          {card4Content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </SwiperSlide>
        </Swiper>
      </Box>
    </>
  );
}
